import { Box, Checkbox, IconButton, Menu, MenuItem, TableCell, Tooltip } from "@material-ui/core";
import React, { useCallback } from "react";
import { Ellipsis, Thumbnail } from "components";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setNewMaterialVersion } from "store/features/materialSpecs/specs";
import { serverAddress } from "utils";
import { MaterialDetailsViewModel } from "api/factories/creatives.factory";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { useMenu, usePermissions } from "hooks";
import { useDownloadMaterials } from "hooks/api";
import { Permission } from "api";
import { PreviousMaterialVersionTableRow } from "pages/MaterialSpecs/components/InfoPanel/Versions/PreviousMaterialVersion/PreviousMaterialVersion.styled";

interface PreviousMaterialVersionProps {
    materialSpecId: number;
    selectedMaterialId: number;
    previousVersion: MaterialDetailsViewModel;
    permissions?: Permission[];
    selected: boolean;
    onVersionSelect(id: number): void;
}

export const PreviousMaterialVersion = ({
    materialSpecId,
    selectedMaterialId,
    previousVersion,
    selected,
    onVersionSelect,
    permissions,
}: PreviousMaterialVersionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { open, anchorEl, handleOpen, handleClose } = useMenu();
    const { DOWNLOAD } = usePermissions(permissions);
    const { refetch } = useDownloadMaterials("AD_MATERIAL", previousVersion.id);
    // TODO: unify date on the server; if server will change format this function will return invalid date
    // TODO: temporarily reverted
    // const modificationDate = parse(previousVersion.modificationDate, "dd-MM-yy HH:mm", new Date());

    const handleMaterialsDownload = useCallback(
        // eslint-disable-next-line
        async (e: any) => {
            handleClose(e);
            await refetch();
        },
        [refetch, handleClose]
    );

    const handleVersionSwitch = useCallback(
        // eslint-disable-next-line
        (material: MaterialDetailsViewModel) => (e: any) => {
            dispatch(setNewMaterialVersion({ material, materialSpecId, selectedMaterialId }));
            onVersionSelect(material.id);
            handleClose(e);
        },
        [dispatch, materialSpecId, selectedMaterialId, onVersionSelect, handleClose]
    );

    return (
        <PreviousMaterialVersionTableRow selected={selected}>
            <TableCell padding="checkbox">
                <Checkbox checked={false} disabled={true} />
            </TableCell>
            <TableCell>{previousVersion.version}</TableCell>
            <TableCell padding="none" align="center">
                <Box width="50px" height="50px" display="flex" alignItems="center" justifyContent="center" p={1}>
                    {previousVersion.materialInfoPreview ? (
                        <Thumbnail src={`${serverAddress}${previousVersion.materialInfoPreview.url}`} />
                    ) : (
                        <Thumbnail src={`${process.env.PUBLIC_URL}/noPhoto.png`} />
                    )}
                </Box>
            </TableCell>
            <TableCell>{previousVersion.modificationDate}</TableCell>
            <TableCell>
                <Tooltip title={previousVersion.inputFileName}>
                    <Ellipsis>{previousVersion.inputFileName}</Ellipsis>
                </Tooltip>
            </TableCell>
            <TableCell>
                <Tooltip title={previousVersion.totalFileSize}>
                    <Ellipsis>{previousVersion.totalFileSize}</Ellipsis>
                </Tooltip>
            </TableCell>
            <TableCell>
                <>
                    <IconButton size="small" aria-haspopup="true" onClick={handleOpen}>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu anchorEl={anchorEl} keepMounted open={open} onClose={handleClose}>
                        {selectedMaterialId !== previousVersion.id && (
                            <MenuItem onClick={handleVersionSwitch(previousVersion)}>
                                {t("buttons.label.setNewVersion")}
                            </MenuItem>
                        )}
                        {DOWNLOAD?.display && (
                            <MenuItem disabled={DOWNLOAD.disabled} onClick={handleMaterialsDownload}>
                                {t("buttons.label.download")}
                            </MenuItem>
                        )}
                    </Menu>
                </>
            </TableCell>
        </PreviousMaterialVersionTableRow>
    );
};
